import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CloseAllTradesModal } from '@pages/Trade/Portfolio/Modals/CloseAllTradesModal'
import { CardTradingGroup } from '@pages/Trade/Portfolio/components/Card-trading'
import { CardTradingView } from '@pages/Trade/Portfolio/components/CardTradingView'
import { useFixTradingWebSocketContext } from '@pages/Trade/Portfolio/context/FixTradingWebSocketContext'
import type { DxtradeInstrumentMap } from '@pages/Trade/Portfolio/hooks/useDxtradeInstruments'
import { useDxtradePortfolioTrades } from '@pages/Trade/Portfolio/hooks/useDxtradePortfolioTrades'
import { useOpenTradesWithLots } from '@pages/Trade/Portfolio/hooks/useOpenTradesWithLots'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { CloseIcon } from '@ui/icons/Close.icon'

import { useWindowResize } from '@utils/domUtils'

import type { DxTradeOpenPositionsResponse } from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

interface WidgetTradesTabProps {
  accountCode: string
  accountCurrency?: CurrencyType
  asset: string
  openPositionsData: DxTradeOpenPositionsResponse
  isOpenPositionsLoading: boolean
  instrumentsMap: DxtradeInstrumentMap
  onOpenPositionsRefresh: () => void
}

export const WidgetTradesTab: React.FC<WidgetTradesTabProps> = ({
  accountCode,
  accountCurrency,
  asset,
  openPositionsData,
  isOpenPositionsLoading,
  instrumentsMap,
  onOpenPositionsRefresh,
}) => {
  const { t } = useTranslation()
  const { closeAllPositions } = useFixTradingWebSocketContext()
  const closeAllTradesModal = useModal(CloseAllTradesModal, { closeAllPositions })
  const isMobile = useWindowResize()

  const {
    trades: openPositionsTrades,
    isLoading: isTradesLoading,
    refreshOrders,
    refreshMetrics,
  } = useDxtradePortfolioTrades(accountCode, openPositionsData)

  const preparedOpenPositions = useOpenTradesWithLots(openPositionsTrades, instrumentsMap, asset)

  const ungroupedGroups = useMemo<CardTradingGroup[]>(
    () =>
      preparedOpenPositions.map((trade) => ({
        symbol: trade.symbol,
        trades: [trade],
      })),
    [preparedOpenPositions]
  )

  const handleCloseAll = () => {
    if (!preparedOpenPositions.length || !accountCode) {
      return
    }

    closeAllTradesModal.open({
      trades: preparedOpenPositions,
      accountCurrency,
      accountCode,
      assetSymbol: asset,
      assetDescription: preparedOpenPositions[0]?.description,
      onSuccess: () => {
        onOpenPositionsRefresh()
        refreshOrders()
        refreshMetrics()
      },
    })
  }

  const isCloseAllDisabled =
    !preparedOpenPositions.length || !accountCode || isOpenPositionsLoading || isTradesLoading

  return (
    <Box flex direction='column' px={isMobile ? 'x0' : 'x16'} pb='x16'>
      <Box mb='x12'>
        <Button
          size='sm'
          variant='danger'
          onClick={handleCloseAll}
          iconLeft={CloseIcon}
          disabled={isCloseAllDisabled}
        >
          {t('Close all')}
        </Button>
      </Box>
      <CardTradingView
        groups={ungroupedGroups}
        isLoading={isOpenPositionsLoading || isTradesLoading}
        accountCurrency={accountCurrency}
        accountCode={accountCode}
        singleTradeLabelMode='side'
        dxtradeInstruments={instrumentsMap}
      />
    </Box>
  )
}
